import {useMemo, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {URLLink} from "../../../../sdo/URLLink.ts";
import WebReferenceCard from "./web/WebReferenceCard.tsx";

let nextId = 100;

function createEmptyURLLink(): URLLink {
    return {
        uid: String(nextId++),
        title: "",
        sourceURL: "",
        summary: "",
    };
}

// TODO: Replace with API call to load URLs from IntelProfileManager.readURLs()
function buildTestData(): URLLink[] {
    return [
        {
            uid: "1",
            title: "March surveillance briefing — partner network feed",
            sourceURL: "https://intel.example.com/briefings/2026-03/surveillance-summary.html",
            summary: "Open-source intelligence brief covering the March observation window. Includes corroborating photographs and witness statements provided through the partner liaison program.",
        },
        {
            uid: "2",
            title: "Public records search results",
            sourceURL: "https://records.example.gov/search?case=2026-04812",
            summary: "Court filings and public records returned from the case lookup. Used to confirm aliases and known associates referenced in the analyst notes.",
        },
        {
            uid: "3",
            title: "Wire service article — incident coverage",
            sourceURL: "https://wire.example.com/2026/04/01/incident-report-eastside.html",
            summary: "",
        },
        {
            uid: "4",
            title: "",
            sourceURL: "https://archive.example.org/snapshot/web-page-id-887421",
            summary: "Archived snapshot of the original web page captured before takedown.",
        },
    ];
}

function UXIntelWebRefsPanel() {
    const [links, setLinks] = useState<URLLink[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [newLinkUid, setNewLinkUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return links;
        const q = filterText.toLowerCase();
        return links.filter((l) => JSON.stringify(l).toLowerCase().includes(q));
    }, [links, filterText]);

    const handleDelete = (uid: string) => {
        // TODO: Replace with API call to IntelProfileManager.deleteURL()
        setLinks((prev) => prev.filter((l) => l.uid !== uid));
        if (newLinkUid === uid) setNewLinkUid(null);
    };

    const handleUpdate = (updated: URLLink) => {
        // TODO: Replace with API call to IntelProfileManager.saveURL()
        setLinks((prev) => prev.map((l) => l.uid === updated.uid ? updated : l));
    };

    const handleNew = () => {
        const newLink = createEmptyURLLink();
        setLinks((prev) => [newLink, ...prev]);
        setNewLinkUid(newLink.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Web References</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter references..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {links.length}
                </Typography>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={handleNew}
                    sx={{...appStyles.panelActionButton, position: "absolute", right: 8}}
                >
                    New Reference
                </Button>
            </Stack>

            {/* Web reference cards list */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((link) => (
                    <WebReferenceCard
                        key={link.uid}
                        link={link}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        startInEditMode={link.uid === newLinkUid}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No web references match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXIntelWebRefsPanel;
